/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-04
 */

// Add third-party dependencies.
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useEffect, useState } from 'react';

// Add local dependencies.
import type { ChangelogData } from '../../../types/changelog';
import {
  getChangelogData,
  getPublishedEntries,
  groupEntriesByYear,
} from '../../../utils/changelogUtils';
import ChangelogYearSection from './ChangelogYearSection';
import Header from '../../layout/Header';
import Footer from '../../layout/Footer';

/**
 * Main changelog page component that displays version history and changes.
 * Shows published changelog entries in reverse chronological order.
 *
 * @returns {React.ReactElement} The changelog page component
 *
 * @example
 * return (
 *   <Changelog />
 * );
 */
const Changelog: React.FC = () => {
  const [changelogData, setChangelogData] = useState<ChangelogData | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadChangelog = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getChangelogData();
        setChangelogData(data);
      } catch (err) {
        console.error('Failed to load changelog:', err);
        setError('Failed to load changelog. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadChangelog();
  }, []);

  // Get published entries only, grouped into collapsible year sections.
  const publishedEntries = changelogData
    ? getPublishedEntries(changelogData.entries)
    : [];
  const yearGroups = groupEntriesByYear(publishedEntries);
  const latestVersion = publishedEntries[0]?.version ?? null;
  const currentYear = String(new Date().getFullYear());
  // Open the current year by default. If there are no entries yet for the
  // current year (e.g. early in a new year), fall back to opening the newest
  // year so the page is never fully collapsed.
  const hasCurrentYear = yearGroups.some(group => group.year === currentYear);

  return (
    <>
      <Header />
      <Container maxWidth="md" sx={{ py: 4, minHeight: '60vh' }}>
        {/* Page header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 700,
              mb: 2,
              color: 'primary.main',
            }}
          >
            Changelog
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            What&apos;s new in tools.dance
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: 'auto' }}
          >
            Stay up to date with the latest features, improvements, and bug
            fixes in our dance studio management platform.
          </Typography>
        </Box>

        {/* Loading state */}
        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 8,
            }}
          >
            <CircularProgress size={40} />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading changelog...
            </Typography>
          </Box>
        )}

        {/* Error state */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 4 }}
            action={
              <Typography
                variant="body2"
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => window.location.reload()}
              >
                Retry
              </Typography>
            }
          >
            {error}
          </Alert>
        )}

        {/* Changelog entries */}
        {!loading && !error && (
          <>
            {publishedEntries.length > 0 ? (
              <Box>
                {yearGroups.map((group, index) => (
                  <ChangelogYearSection
                    key={group.year}
                    year={group.year}
                    entries={group.entries}
                    latestVersion={latestVersion}
                    defaultExpanded={
                      hasCurrentYear ? group.year === currentYear : index === 0
                    }
                  />
                ))}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                  No changelog entries found
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Check back later for updates about new features and
                  improvements.
                </Typography>
              </Box>
            )}

            {/* Footer info */}
            {changelogData && publishedEntries.length > 0 && (
              <Box
                sx={{
                  mt: 6,
                  pt: 4,
                  borderTop: 1,
                  borderColor: 'divider',
                  textAlign: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Showing {publishedEntries.length} published version
                  {publishedEntries.length !== 1 ? 's' : ''}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Last updated:{' '}
                  {new Date(
                    changelogData.meta.lastUpdated
                  ).toLocaleDateString()}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Container>
      <Footer />
    </>
  );
};

export default Changelog;
